import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Clock, FileText, Gift } from 'lucide-react'
import 'rsuite/dist/rsuite.min.css'

import routes from 'core/routes'
import { api } from 'core/services/api'
import { orulTokenManager } from 'core/services/orul-token-manager'

import { TRootReducer } from 'store'

import { SweepstakeModal } from './components/sweepstakeModal'
import { TicketModal } from './components/ticketModal'
import {
  Content,
  CardDescription,
  CardDates,
  CardInfo,
  CardMetaRow,
  EmptyState,
  SweepstakeCard,
  SweepstakeCardHeader,
  SweepstakeCardHeaderIcon,
  SweepstakeCardHeaderText,
  StatusBadge,
  Wrapper,
  RegulationButton,
  RegulationLink,
} from './styles'
import { TSweepstake, TLuckyNumber, TPrize } from './types'

export function SweepstakesView() {
  const formatDate = (startDate: Date, endDate: Date) =>
    `${startDate.getDate()}/${
      startDate.getMonth() + 1
    }/${startDate.getFullYear()} - ${endDate.getDate()}/${
      endDate.getMonth() + 1
    }/${endDate.getFullYear()}`

  const statusLabels = {
    active: 'Ativo',
    inactive: 'Inativo',
    finished: 'Finalizado',
    canceled: 'Cancelado',
  }

  // Mock data para visualização

  const phoneNumber = useSelector((state: any) =>
    state.user.attributes.phone_number.replace('+', ''),
  )
  const { currentOrg } = useSelector((state: TRootReducer) => state.app)
  const [sweepstakes, setSweepstakes] = useState<TSweepstake[]>([])
  const [selectedSweepstake, setSelectedSweepstake] =
    useState<TSweepstake | null>(null)
  const [numberModalContext, setNumberModalContext] = useState<{
    sweepstake: TSweepstake
    luckyNumber: TLuckyNumber
  } | null>(null)
  const [prize, setPrize] = useState<TPrize | null>(null)
  useEffect(() => {
    const fetchSweepstakes = async () => {
      try {
        const response = await api.get<TSweepstake[]>(
          routes.api.sweepstakes.find(phoneNumber),
          {
            headers: {
              'X-App-Token': currentOrg?.token || '',
              authorization: `Bearer ${orulTokenManager.getToken()}`,
            },
          },
        )
        setSweepstakes(response.data)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching sweepstakes:', error)
      }
    }
    fetchSweepstakes()
  }, [phoneNumber])

  return (
    <Wrapper>
      <Content>
        {sweepstakes.length === 0 ? (
          <EmptyState>Nenhum sorteio encontrado.</EmptyState>
        ) : (
          sweepstakes.map(sweepstake => {
            const hasWonCurrentSweepstake = sweepstake.won_sweepstakes?.some(
              item =>
                item.sweepstake_id === sweepstake.sweepstake_id && item.has_won,
            )

            return (
              <SweepstakeCard
                key={sweepstake.sweepstake_id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelectedSweepstake(sweepstake)
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setSelectedSweepstake(sweepstake)
                  }
                }}
              >
                <SweepstakeCardHeader>
                  <SweepstakeCardHeaderIcon>
                    <Gift size={36} color="#fccf2b" strokeWidth={2} />
                  </SweepstakeCardHeaderIcon>
                  <CardInfo>
                    <SweepstakeCardHeaderText>
                      {sweepstake.name}
                    </SweepstakeCardHeaderText>
                    <CardDescription>{sweepstake.description}</CardDescription>
                    <CardMetaRow>
                      <CardDates>
                        <Clock size={14} color="#64748b" />
                        <span>
                          {formatDate(
                            new Date(sweepstake.start_at),
                            new Date(sweepstake.end_at),
                          )}
                        </span>
                      </CardDates>
                      <StatusBadge $variant={sweepstake.status}>
                        {statusLabels[sweepstake.status]}
                      </StatusBadge>
                    </CardMetaRow>
                    {hasWonCurrentSweepstake && (
                      <span
                        style={{
                          marginTop: '4px',
                          display: 'inline-block',
                          color: '#6CA651',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                        }}
                      >
                        Parabéns! Você é um dos ganhadores neste sorteio!
                      </span>
                    )}
                    {sweepstake.regulation_file_url && (
                      <RegulationButton
                        onClick={e => {
                          e.stopPropagation()
                        }}
                        style={{ marginRight: 'auto' }}
                      >
                        <RegulationLink
                          href={`${process.env.REACT_APP_CDN}/${sweepstake.regulation_file_url}`}
                        >
                          <FileText size={14} color="#64748b" />
                          <span>Regulamento</span>
                        </RegulationLink>
                      </RegulationButton>
                    )}
                  </CardInfo>
                </SweepstakeCardHeader>
              </SweepstakeCard>
            )
          })
        )}
      </Content>
      <SweepstakeModal
        selectedSweepstake={selectedSweepstake}
        setSelectedSweepstake={setSelectedSweepstake}
        setNumberModalContext={setNumberModalContext}
        setWinnerNumberPrize={setPrize}
      />
      <TicketModal
        numberModalContext={numberModalContext}
        setNumberModalContext={setNumberModalContext}
        formatDate={(startDate, endDate) =>
          formatDate(new Date(startDate), new Date(endDate))
        }
        statusLabels={statusLabels}
        prize={prize}
      />
    </Wrapper>
  )
}
